import { type GroupChannel as SendbirdGroupChannel } from '@sendbird/chat/groupChannel';
import type { FileMessageCreateParams } from '@sendbird/chat/message';
import GroupChannel from '@sendbird/uikit-react/GroupChannel';
import React from 'react';
import { View } from 'smoothie';

import { useInitLocalMessageDrafts } from '../hooks/useLocalMessageDrafts';
import { ChannelHeader } from './ChannelHeader';
import { MessageInputWrapper } from './MessageInputWrapper';

interface GuideMessagingChannelProps {
    sendbirdChannel: SendbirdGroupChannel | null;
    headerTextSize?: 'small' | 'large';
    shouldShowLinkToMembershipDetails?: boolean;
}

export const GuideMessagingChannel = ({
    sendbirdChannel,
    headerTextSize = 'large',
    shouldShowLinkToMembershipDetails = false,
}: GuideMessagingChannelProps) => {
    const channelUrl = sendbirdChannel?.url ?? '';
    const { draftMessage, setDraftMessage, clearDraftMessage } =
        useInitLocalMessageDrafts(channelUrl);

    return (
        <View className="sendbird-app__conversation-wrap" height="100%">
            {sendbirdCssOverrides}
            <GroupChannel
                channelUrl={channelUrl}
                renderChannelHeader={() => (
                    <ChannelHeader
                        currentChannel={sendbirdChannel}
                        textSize={headerTextSize}
                        shouldShowLinkToMembershipDetails={
                            shouldShowLinkToMembershipDetails
                        }
                    />
                )}
                renderMessageInput={() => (
                    <MessageInputWrapper
                        channelUrl={channelUrl}
                        initialMessage={draftMessage}
                        onMessageChange={setDraftMessage}
                    />
                )}
                disableUserProfile={true} // Disables modal popup when clicking any user avatar in channel.
                replyType="NONE" // Disables reply/thread functionality.
                isMultipleFilesMessageEnabled={true} // Only enable if all clients can render multiple file messages.
                onBeforeSendUserMessage={(params) => {
                    clearDraftMessage();
                    let cleanedMessage = params.message;

                    // Strip zero-width spaces from message to prevent broken links when copying/pasting
                    cleanedMessage = cleanedMessage.replace(/[\u200B]/g, '');

                    return {
                        ...params,
                        message: cleanedMessage,
                    };
                }}
                onBeforeSendFileMessage={(params: FileMessageCreateParams) => {
                    // Extract file details.
                    const { fileName, file } = params;
                    let displayFileName: string = fileName ?? '';
                    // Fallback value for file name, since fileName seems to not populate in most cases.
                    if (fileName == null || fileName === '') {
                        if (file && 'name' in file) {
                            displayFileName = file.name;
                        }
                    }

                    // Use the browser's confirmation dialog to prompt user to confirm the upload should proceed.
                    if (
                        confirm(
                            `Are you sure you want to upload the file${displayFileName !== '' ? `:${displayFileName}` : ''}?`,
                        )
                    ) {
                        return { file };
                    }
                    return {};
                }}
            />
        </View>
    );
};

// Override styling of Sendbird UIKit GroupChannel components.
const sendbirdCssOverrides = (
    <style type="text/css">
        {`
            /**
             * Override default padding around message input to be smaller
             */
            .sendbird-message-input-wrapper .sendbird-message-input-wrapper__message-input {
                padding: 0;
                padding-left: 16px;
                padding-right: 36px; /* space for the send button (see next rule) */
            }

            /**
             * Move the send/attach buttons to the right of the message input.
             */
            .sendbird-message-input-wrapper .sendbird-message-input .sendbird-iconbutton {
                right: -36px;
            }

            /**
             * There is no default padding on this input, so add some
             */
            .sendbird-message-input-wrapper .sendbird-message-input-wrapper__message-input #sendbird-message-input-text-field {
                padding: 8px;
            }

            /**
             * Match padding from rule above, and align placeholder with cursor
             */
            .sendbird-message-input-wrapper .sendbird-message-input-wrapper__message-input .sendbird-message-input--placeholder {
                padding: 8px;
                top: -4px;
                left: 0;
            }

            /**
             * Allow message input height to expand based on content up to a max height.
             */
            .sendbird-message-input .sendbird-message-input--textarea {
                height: auto;
                max-height: 300px;
                overflow-y: auto;
                scrollbar-width: thin;
            }
            
            /**
             * Hide the message content menu kebab button if there are no available options. This state occurs on received/incoming file messages.
             */
            .sendbird-message-content__middle:has(> .sendbird-message-content__middle__body-container > .sendbird-thumbnail-message-item-body) + .sendbird-message-content__right {
                display: none !important;
             }

            /**
             * Increase space and add a separator border between two consecutive file messages.
             */
            .sendbird-msg-hoc:has(.sendbird-message-content__middle > .sendbird-message-content__middle__body-container > .sendbird-thumbnail-message-item-body) + .sendbird-msg-hoc:has(.sendbird-message-content__middle > .sendbird-message-content__middle__body-container > .sendbird-thumbnail-message-item-body) .sendbird-message-content__middle {
                padding-top: 16px;
                border-top: 1px solid lightgray;
                margin-top: 16px;
            }

            /**
             * Removing border around the component to fit better within our styled containers
             */
            .sendbird-app__conversation-wrap .sendbird-conversation {
                border-width: 0;
            }
        `}
    </style>
);
